<!-- src/views/UserProfileView.vue -->
<template>
  <div class="min-h-screen font-sans selection:bg-[#20FCF1]/30 text-left">
    
    <!-- 1. REGISTRY LOADING STATE -->
    <div v-if="isDataLoading" class="flex flex-col items-center justify-center py-32">
      <div class="w-12 h-12 border-4 border-[#0A1630] border-t-[#20FCF1] rounded-full animate-spin"></div>
      <p class="text-[10px] font-black mt-6 text-slate-400 uppercase tracking-[0.3em]">Initialising Individual Registry...</p>
    </div>

    <!-- 2. MAIN PROFILE CONTENT -->
    <div v-else class="max-w-7xl mx-auto space-y-8 animate-fadeIn text-left p-4 sm:p-6 md:p-8">
      
      <!-- BRANCH A: SUPER ADMIN SECURITY NODE -->
      <div v-if="isSuperAdminProfile" class="max-w-7xl mx-auto space-y-8 animate-fadeIn text-left">
        <!-- Header Card with Mint Anchor Line -->
        <div class="bg-[#0A1630] p-8 md:p-10 rounded-[40px] shadow-2xl border-b-4 border-[#20FCF1] relative overflow-hidden">
          <div class="absolute top-0 right-0 p-12 opacity-5 pointer-events-none">
            <ShieldCheckIcon class="h-32 w-32 text-white" />
          </div>
          
          <div class="relative z-10 flex flex-col md:flex-row gap-8 justify-between items-center">
            <div class="flex flex-col md:flex-row items-center gap-8 text-center md:text-left">
              <div class="relative">
                <img 
                  v-if="displayedProfile?.photoUrl"
                  :src="displayedProfile.photoUrl" 
                  class="h-32 w-32 rounded-[32px] object-cover border-4 border-white/10 shadow-2xl" 
                  alt="Super Admin Profile"
                >
                <div v-else class="h-32 w-32 rounded-[32px] bg-white/10 flex items-center justify-center border-4 border-white/10 shadow-2xl">
                  <UserIcon class="h-16 w-16 text-white" />
                </div>
              </div>
              <div class="space-y-3 text-left">
                <h1 class="text-4xl font-black text-white uppercase tracking-tight leading-none">
                  {{ displayedProfile?.firstName || 'System' }} {{ displayedProfile?.lastName || 'Supervisor' }}
                </h1>
                <span class="text-[10px] font-black text-[#20FCF1] uppercase tracking-[0.2em] bg-white/5 px-3 py-1 rounded-lg border border-white/10 inline-block">
                  Master Registry Authority
                </span>
              </div>
            </div>
            
            <button 
              @click="handleLogout" 
              class="bg-rose-500/10 border border-rose-500/20 text-rose-400 px-8 py-4 rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-rose-600 hover:text-white transition-all shadow-lg"
            >
              Initialise Logout
            </button>
          </div>
        </div>

        <!-- Master Security Profile Card -->
        <div class="bg-white p-10 rounded-[40px] shadow-2xl border border-slate-100">
          <div class="flex items-center gap-4 mb-8 border-b pb-6 text-left">
            <UserIcon class="h-6 w-6 text-[#0A1630]" />
            <div>
              <h2 class="text-[11px] font-black text-[#0A1630] uppercase tracking-[0.3em]">Master Security Profile</h2>
              <p class="text-[9px] text-slate-400 font-bold uppercase mt-1">Uprooted Supervisor Account Node</p>
            </div>
          </div>

          <!-- Mode 1: Static Read / Option Selection -->
          <div v-if="!isEditingSuperAdmin" class="grid grid-cols-1 md:grid-cols-3 gap-8">
            <!-- Profile Photo Segment -->
            <div class="space-y-4 flex flex-col items-center p-6 bg-slate-50 rounded-[32px] border border-slate-100">
              <span class="text-[9px] font-black text-slate-400 uppercase tracking-widest">Avatar Signature</span>
              <div class="relative group">
                <img 
                  v-if="displayedProfile?.photoUrl"
                  :src="displayedProfile.photoUrl" 
                  class="h-28 w-28 rounded-[24px] object-cover border-2 border-slate-200 shadow-sm" 
                  alt="Avatar"
                >
                <div v-else class="h-28 w-28 rounded-[24px] bg-slate-200 flex items-center justify-center border border-slate-300">
                  <UserIcon class="h-12 w-12 text-slate-400" />
                </div>
              </div>
              
              <button 
                @click="triggerFileInput"
                :disabled="isUploadingImage"
                class="w-full text-center bg-[#0A1630] text-white py-3 rounded-2xl text-[9px] font-black uppercase tracking-wider hover:bg-slate-800 transition-all shadow-sm flex items-center justify-center gap-2"
              >
                <span v-if="isUploadingImage" class="animate-pulse">Uploading...</span>
                <span v-else>Upload Photo</span>
              </button>
              
              <!-- Hidden Image Input Element -->
              <input 
                type="file" 
                ref="fileInput" 
                class="hidden" 
                accept="image/*" 
                @change="handleImageUpload" 
              />
            </div>

            <!-- Credentials Segment -->
            <div class="md:col-span-2 space-y-6 flex flex-col justify-between text-left">
              <!-- Email Field -->
              <div class="space-y-2">
                <label class="block text-[10px] font-black text-slate-400 uppercase tracking-widest">Registered Email Address</label>
                <div class="w-full bg-slate-50 border border-slate-200 rounded-2xl px-5 py-4 text-xs font-bold text-slate-700 select-all">
                  {{ displayedProfile?.email || 'N/A' }}
                </div>
                <p class="text-[9px] text-slate-400 font-medium italic">This address serves as your primary login identifier. To modify this profile email, contact engineering security.</p>
              </div>

              <!-- Password Actions -->
              <div class="space-y-3 pt-4 border-t border-slate-100">
                <label class="block text-[10px] font-black text-slate-400 uppercase tracking-widest">Security Credentials</label>
                <div class="flex flex-col sm:flex-row gap-3">
                  <button 
                    @click="triggerPasswordReset" 
                    :disabled="isSendingReset"
                    class="flex-1 bg-[#20FCF1] text-[#0A1630] py-4 rounded-2xl text-[10px] font-black uppercase tracking-wider hover:scale-105 active:scale-95 transition-all text-center shadow-md"
                  >
                    <span v-if="isSendingReset">Transmitting Reset Request...</span>
                    <span v-else>Send Password Reset Email</span>
                  </button>
                  
                  <button 
                    @click="startEditingSuperAdmin" 
                    class="flex-1 bg-white border border-slate-200 text-[#0A1630] py-4 rounded-2xl text-[10px] font-black uppercase tracking-wider hover:bg-slate-50 transition-all text-center shadow-sm"
                  >
                    Update Profile Details
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Mode 2: Inline Registry Editor -->
          <div v-else class="space-y-6 text-left">
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div>
                <label class="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-1">First Name</label>
                <input 
                  v-model="superAdminEditForm.firstName" 
                  type="text" 
                  class="w-full bg-slate-50 border border-slate-200 rounded-2xl px-5 py-4 text-xs font-bold text-[#0A1630] outline-none focus:ring-2 focus:ring-[#20FCF1]"
                />
              </div>
              <div>
                <label class="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-1">Last Name</label>
                <input 
                  v-model="superAdminEditForm.lastName" 
                  type="text" 
                  class="w-full bg-slate-50 border border-slate-200 rounded-2xl px-5 py-4 text-xs font-bold text-[#0A1630] outline-none focus:ring-2 focus:ring-[#20FCF1]"
                />
              </div>
            </div>
            
            <!-- Hybrid File Upload & Paste input container -->
            <div>
              <label class="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-1">Profile Photo Signature</label>
              <div class="flex flex-col sm:flex-row gap-4 items-center bg-slate-50 p-4 rounded-2xl border border-slate-200">
                <div class="relative shrink-0">
                  <img 
                    v-if="superAdminEditForm.photoUrl"
                    :src="superAdminEditForm.photoUrl" 
                    class="h-16 w-16 rounded-xl object-cover border border-slate-200 shadow-sm" 
                    alt="Preview"
                  >
                  <div v-else class="h-16 w-16 rounded-xl bg-slate-200 flex items-center justify-center border border-slate-300">
                    <UserIcon class="h-6 w-6 text-slate-400" />
                  </div>
                </div>
                <div class="flex-1 w-full space-y-3 text-left">
                  <button 
                    type="button"
                    @click="triggerFileInput"
                    :disabled="isUploadingImage"
                    class="px-5 py-2.5 bg-[#0A1630] text-white rounded-xl text-[9px] font-black uppercase tracking-wider hover:bg-slate-800 transition-all shadow-sm flex items-center gap-2"
                  >
                    <span v-if="isUploadingImage">Uploading Photo...</span>
                    <span v-else>Upload Photo File</span>
                  </button>
                  <input 
                    v-model="superAdminEditForm.photoUrl" 
                    type="text" 
                    placeholder="Or paste an external web photo URL..."
                    class="w-full bg-white border border-slate-200 rounded-xl px-4 py-2 text-[11px] font-mono text-[#0A1630] outline-none focus:ring-2 focus:ring-[#20FCF1]"
                  />
                  
                  <input 
                    type="file" 
                    ref="fileInput" 
                    class="hidden" 
                    accept="image/*" 
                    @change="handleImageUpload" 
                  />
                </div>
              </div>
            </div>

            <div class="flex justify-end gap-3 pt-6 border-t border-slate-100">
              <button 
                @click="isEditingSuperAdmin = false" 
                :disabled="isSavingSuperAdmin"
                class="px-6 py-3 bg-white border border-slate-200 text-slate-500 rounded-2xl text-[10px] font-black uppercase tracking-wider hover:bg-slate-50 transition-all shadow-sm"
              >
                Cancel
              </button>
              <button 
                @click="saveSuperAdminProfile" 
                :disabled="isSavingSuperAdmin"
                class="px-8 py-3 bg-[#20FCF1] text-[#0A1630] rounded-2xl text-[10px] font-black uppercase tracking-wider hover:scale-105 active:scale-95 transition-all shadow-md flex items-center gap-2"
              >
                <div v-if="isSavingSuperAdmin" class="animate-spin h-3 w-3 border-2 border-[#0A1630] border-t-transparent rounded-full"></div>
                <span>{{ isSavingSuperAdmin ? 'Synchronising...' : 'Save Registry Changes' }}</span>
              </button>
            </div>
          </div>
        </div>

        <!-- PERFORMANCE & FEEDBACK HUB FOR SUPER ADMINS -->
        <div class="bg-white p-10 rounded-[40px] shadow-2xl border border-slate-100 animate-fadeIn space-y-10">
          <div class="flex items-center gap-4 border-b pb-6 text-left">
              <StarIcon class="h-6 w-6 text-amber-400" />
              <h2 class="text-[11px] font-black text-[#0A1630] uppercase tracking-[0.3em]">Performance & Feedback Hub</h2>
          </div>

          <!-- CARD 1: USER ID REVIEW QR -->
          <div class="grid grid-cols-1 md:grid-cols-2 gap-12 items-center text-left">
             <div class="flex flex-col items-center gap-6">
                <div class="bg-[#0A1630] p-4 rounded-[32px] shadow-2xl border-4 border-[#20FCF1]/20">
                  <div class="bg-white p-4 rounded-2xl">
                    <img v-if="reviewQrUrl" :src="reviewQrUrl" alt="Review QR" class="w-32 h-32 grayscale hover:grayscale-0 transition-all duration-500" />
                  </div>
                </div>
                <div class="text-center">
                  <div class="flex items-center justify-center gap-3">
                      <StarIcon class="h-6 w-6 text-amber-400 fill-amber-400" />
                      <span class="text-4xl font-black text-[#0A1630] tracking-tighter">{{ displayedProfile?.averageRating?.toFixed(1) || '0.0' }}</span>
                  </div>
                  <p class="text-[9px] font-black text-slate-300 uppercase mt-2 tracking-widest">{{ displayedProfile?.reviewCount || 0 }} Verified Submissions</p>
                </div>
             </div>

             <div class="space-y-6 text-center md:text-left">
                <h3 class="text-xl font-black text-[#0A1630] uppercase tracking-tight leading-tight">Digital Capability Index</h3>
                <p class="text-xs text-slate-500 font-medium leading-relaxed">Present this code to Site Managers for on-site assessment. Verified feedback is recorded directly into your permanent competency trail.</p>
                
                <div class="flex flex-col gap-3">
                  <button @click="shareViaEmail" class="w-full bg-[#0A1630] text-white px-6 py-4 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-slate-800 transition-all flex items-center justify-center gap-3 shadow-md">
                    <EnvelopeIcon class="h-4 w-4 text-[#20FCF1]" /> Email Link to Manager
                  </button>
                  
                  <button @click="copyReviewLink" class="w-full bg-white border border-slate-200 text-[#0A1630] px-6 py-4 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-slate-50 transition-all flex items-center justify-center gap-3 shadow-sm">
                    <ClipboardIcon class="h-4 w-4 text-[#20FCF1]" />
                    <span>{{ copiedText }}</span>
                  </button>
                </div>
             </div>
          </div>

          <!-- CARD 2: CLIENT FEEDBACK QUESTIONNAIRE QR -->
          <div class="pt-10 border-t border-slate-100 grid grid-cols-1 md:grid-cols-2 gap-12 items-center text-left">
             <div class="flex flex-col items-center gap-6">
                <div class="bg-[#0A1630] p-4 rounded-[32px] shadow-2xl border-4 border-emerald-400/20">
                  <div class="bg-white p-4 rounded-2xl">
                    <img v-if="clientFeedbackQrUrl" :src="clientFeedbackQrUrl" alt="Client Feedback QR" class="w-32 h-32 grayscale hover:grayscale-0 transition-all duration-500" />
                  </div>
                </div>
                <div class="text-center">
                  <span class="text-[9px] font-black text-emerald-600 uppercase tracking-widest bg-emerald-50 px-3 py-1 rounded-full border border-emerald-100">Project Closeout</span>
                </div>
             </div>

             <div class="space-y-6 text-center md:text-left">
                <h3 class="text-xl font-black text-[#0A1630] uppercase tracking-tight leading-tight">Client Feedback Questionnaire</h3>
                <p class="text-xs text-slate-500 font-medium leading-relaxed">Present this code to the Client Site Manager upon project completion so they can evaluate service ratings, performance, and site standards.</p>
                
                <div class="flex flex-col gap-3">
                  <button @click="shareFeedbackViaEmail" class="w-full bg-[#0A1630] text-white px-6 py-4 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-slate-800 transition-all flex items-center justify-center gap-3 shadow-md">
                    <EnvelopeIcon class="h-4 w-4 text-emerald-400" /> Email Questionnaire Link
                  </button>
                  
                  <button @click="copyFeedbackLink" class="w-full bg-white border border-slate-200 text-[#0A1630] px-6 py-4 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-slate-50 transition-all flex items-center justify-center gap-3 shadow-sm">
                    <ClipboardIcon class="h-4 w-4 text-emerald-400" />
                    <span>{{ copiedFeedbackText }}</span>
                  </button>
                </div>
             </div>
          </div>
        </div>
      </div>

      <!-- BRANCH B: STANDARD OPERATIVE PROFILE -->
      <div v-else class="max-w-7xl mx-auto space-y-8 animate-fadeIn text-left">
        
        <!-- PREMIUM HEADER CARD (Identity Hub) -->
        <div class="bg-[#0A1630] p-8 md:p-10 rounded-[40px] shadow-2xl border-b-4 border-[#20FCF1] relative overflow-hidden">
          <div class="absolute top-0 right-0 p-12 opacity-5 pointer-events-none">
            <UserIcon class="h-32 w-32 text-white" />
          </div>
          
          <div class="relative z-10 flex flex-col xl:flex-row gap-8 justify-between items-center">
            <div class="flex flex-col md:flex-row items-center gap-8 text-center md:text-left">
              <div v-if="displayedProfile?.photoUrl" class="relative group">
                <img 
                  :src="displayedProfile.photoUrl" 
                  class="h-32 w-32 rounded-[32px] object-cover border-4 border-white/10 shadow-2xl transition-transform group-hover:scale-105" 
                  alt="Profile"
                >
                <div 
                  v-if="isPersonalProfileView && userStore.unreadNotificationCount > 0" 
                  class="absolute -top-3 -right-3 h-8 w-8 bg-rose-600 text-white rounded-full flex items-center justify-center text-xs font-black border-4 border-[#0A1630] shadow-xl pulse-animation"
                >
                  {{ userStore.unreadNotificationCount }}
                </div>
              </div>
              
              <div class="space-y-3">
                <h1 class="text-4xl font-black text-white uppercase tracking-tight leading-none">
                  {{ displayedProfile?.firstName }} {{ displayedProfile?.lastName }}
                </h1>
                <div class="flex flex-wrap items-center gap-3 justify-center md:justify-start">
                  <span class="text-[10px] font-black text-[#20FCF1] uppercase tracking-[0.2em] bg-white/5 px-3 py-1 rounded-lg border border-white/10">
                    {{ formattedJobTitle }}
                  </span>
                  
                  <span v-if="displayedProfile?.annualLeaveStatus" class="bg-amber-500 text-[#0A1630] px-4 py-1 rounded-full text-[9px] font-black uppercase tracking-widest shadow-lg flex items-center gap-2">
                    <SunIcon class="h-3 w-3" /> Authorised Absence
                  </span>

                  <span 
                    :class="isUserActive ? 'bg-emerald-500 text-white' : 'bg-amber-500 text-white'" 
                    class="px-4 py-1 rounded-full text-[9px] font-black uppercase tracking-widest shadow-lg"
                  >
                    {{ isUserActive ? 'Protocol: Active' : (displayedProfile?.status === 'pending_onboarding' ? 'Status: Pending Onboarding' : 'Status: Pending Review') }}
                  </span>
                </div>
              </div>
            </div>

            <!-- PRIMARY ACTIONS -->
            <div class="flex flex-wrap gap-3 no-print justify-center">
              <router-link 
                v-if="isPersonalProfileView" 
                to="/review-me" 
                class="bg-[#20FCF1] text-[#0A1630] px-8 py-4 rounded-2xl text-[10px] font-black uppercase tracking-widest shadow-xl hover:scale-105 active:scale-95 transition-all flex items-center gap-2"
              >
                <QrCodeIcon class="h-4 w-4" /> My ID QR
              </router-link>

              <router-link 
                :to="editProfileRoute" 
                class="bg-white/10 border border-white/10 text-white px-8 py-4 rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-white/20 transition-all shadow-lg flex items-center gap-2"
              >
                <PencilSquareIcon class="h-4 w-4" /> Edit Registry
              </router-link>

              <!-- ADMINISTRATIVE MFA EMERGENCY RECOVERY SHIELD -->
              <button 
                v-if="canManageMfa && !isPersonalProfileView" 
                @click="triggerMfaReset" 
                :disabled="isResettingMfa"
                class="bg-amber-500 text-[#0A1630] px-8 py-4 rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-amber-600 transition-all shadow-xl flex items-center gap-2 disabled:opacity-50"
              >
                <div v-if="isResettingMfa" class="animate-spin h-4 w-4 border-2 border-[#0A1630] border-t-transparent rounded-full"></div>
                <ShieldCheckIcon v-else class="h-4 w-4 text-[#0A1630]" />
                <span>{{ isResettingMfa ? 'Resetting MFA...' : 'Emergency Reset MFA' }}</span>
              </button>

              <button 
                v-if="isPersonalProfileView" 
                @click="handleLogout" 
                class="bg-rose-500/10 border border-rose-500/20 text-rose-400 px-8 py-4 rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-rose-600 hover:text-white transition-all"
              >
                Initialise Logout
              </button>
              
              <button 
                v-if="!isPersonalProfileView" 
                @click="$router.go(-1)" 
                class="bg-white/5 text-white px-8 py-4 rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-white/10 transition-all border border-white/10 shadow-lg"
              >
                &larr; Return to Directory
              </button>
            </div>
          </div>
        </div>

        <div class="grid grid-cols-1 lg:grid-cols-3 gap-10">
          
          <!-- LEFT COLUMN: Operations & Feedback -->
          <div class="lg:col-span-2 space-y-10">

            <!-- AUTHORISED REGISTRY HUB -->
            <div v-if="isPersonalProfileView" class="bg-white rounded-[40px] shadow-2xl border border-slate-100 overflow-hidden animate-fadeIn">
              <div class="p-8 border-b border-slate-50 flex justify-between items-center bg-slate-50/50">
                <div class="flex items-center gap-4 text-left">
                  <div class="w-2 h-2 rounded-full bg-[#20FCF1] animate-pulse"></div>
                  <h2 class="text-[11px] font-black text-[#0A1630] uppercase tracking-[0.3em]">Operational Registry Hub</h2>
                </div>
                <button 
                  v-if="userStore.unreadNotificationCount > 0"
                  @click="clearAllNotifications" 
                  class="text-[9px] font-black text-rose-500 uppercase tracking-widest hover:underline transition-all"
                >
                  Clear Notifications
                </button>
              </div>
              
              <div class="max-h-[440px] overflow-y-auto p-2 no-scrollbar">
                <PendingActionsPanel 
                  :targetUserId="displayedProfile?.userId" 
                  @update-count="handleUpdateCount" 
                  @remove-notification="userStore.removeNotification"
                />
                <div v-if="!userStore.unreadNotificationCount" class="py-20 text-center">
                   <ShieldCheckIcon class="h-16 w-16 text-slate-100 mx-auto mb-4" />
                   <p class="text-[10px] font-black uppercase text-slate-300 tracking-widest">Registry Synchronised • No Outstanding Actions</p>
                </div>
              </div>
            </div>

            <!-- PERFORMANCE & FEEDBACK HUB -->
            <div class="bg-white p-10 rounded-[40px] shadow-2xl border border-slate-100 animate-fadeIn space-y-10">
              <div class="flex items-center gap-4 border-b pb-6 text-left">
                  <StarIcon class="h-6 w-6 text-amber-400" />
                  <h2 class="text-[11px] font-black text-[#0A1630] uppercase tracking-[0.3em]">Performance & Feedback Hub</h2>
              </div>

              <!-- CARD 1: USER ID REVIEW QR -->
              <div class="grid grid-cols-1 md:grid-cols-2 gap-12 items-center text-left">
                 <div class="flex flex-col items-center gap-6">
                    <div class="bg-[#0A1630] p-4 rounded-[32px] shadow-2xl border-4 border-[#20FCF1]/20">
                      <div class="bg-white p-4 rounded-2xl">
                        <img v-if="reviewQrUrl" :src="reviewQrUrl" alt="Review QR" class="w-32 h-32 grayscale hover:grayscale-0 transition-all duration-500" />
                      </div>
                    </div>
                    <div class="text-center">
                      <div class="flex items-center justify-center gap-3">
                          <StarIcon class="h-6 w-6 text-amber-400 fill-amber-400" />
                          <span class="text-4xl font-black text-[#0A1630] tracking-tighter">{{ displayedProfile?.averageRating?.toFixed(1) || '0.0' }}</span>
                      </div>
                      <p class="text-[9px] font-black text-slate-300 uppercase mt-2 tracking-widest">{{ displayedProfile?.reviewCount || 0 }} Verified Submissions</p>
                    </div>
                 </div>

                 <div class="space-y-6 text-center md:text-left">
                    <h3 class="text-xl font-black text-[#0A1630] uppercase tracking-tight leading-tight">Digital Capability Index</h3>
                    <p class="text-xs text-slate-500 font-medium leading-relaxed">Present this code to Site Managers for on-site assessment. Verified feedback is recorded directly into your permanent competency trail.</p>
                    
                    <div class="flex flex-col gap-3">
                      <button @click="shareViaEmail" class="w-full bg-[#0A1630] text-white px-6 py-4 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-slate-800 transition-all flex items-center justify-center gap-3 shadow-md">
                        <EnvelopeIcon class="h-4 w-4 text-[#20FCF1]" /> Email Link to Manager
                      </button>
                      
                      <button @click="copyReviewLink" class="w-full bg-white border border-slate-200 text-[#0A1630] px-6 py-4 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-slate-50 transition-all flex items-center justify-center gap-3 shadow-sm">
                        <ClipboardIcon class="h-4 w-4 text-[#20FCF1]" />
                        <span>{{ copiedText }}</span>
                      </button>
                    </div>
                 </div>
              </div>

              <!-- 🛡️ SEPARATED CARD 2: CLIENT FEEDBACK QUESTIONNAIRE QR -->
              <div class="pt-10 border-t border-slate-100 grid grid-cols-1 md:grid-cols-2 gap-12 items-center text-left">
                 <div class="flex flex-col items-center gap-6">
                    <div class="bg-[#0A1630] p-4 rounded-[32px] shadow-2xl border-4 border-emerald-400/20">
                      <div class="bg-white p-4 rounded-2xl">
                        <img v-if="clientFeedbackQrUrl" :src="clientFeedbackQrUrl" alt="Client Feedback QR" class="w-32 h-32 grayscale hover:grayscale-0 transition-all duration-500" />
                      </div>
                    </div>
                    <div class="text-center">
                      <span class="text-[9px] font-black text-emerald-600 uppercase tracking-widest bg-emerald-50 px-3 py-1 rounded-full border border-emerald-100">Project Closeout</span>
                    </div>
                 </div>

                 <div class="space-y-6 text-center md:text-left">
                    <h3 class="text-xl font-black text-[#0A1630] uppercase tracking-tight leading-tight">Client Feedback Questionnaire</h3>
                    <p class="text-xs text-slate-500 font-medium leading-relaxed">Present this code to the Client Site Manager upon project completion so they can evaluate service ratings, performance, and site standards.</p>
                    
                    <div class="flex flex-col gap-3">
                      <button @click="shareFeedbackViaEmail" class="w-full bg-[#0A1630] text-white px-6 py-4 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-slate-800 transition-all flex items-center justify-center gap-3 shadow-md">
                        <EnvelopeIcon class="h-4 w-4 text-emerald-400" /> Email Questionnaire Link
                      </button>
                      
                      <button @click="copyFeedbackLink" class="w-full bg-white border border-slate-200 text-[#0A1630] px-6 py-4 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-slate-50 transition-all flex items-center justify-center gap-3 shadow-sm">
                        <ClipboardIcon class="h-4 w-4 text-emerald-400" />
                        <span>{{ copiedFeedbackText }}</span>
                      </button>
                    </div>
                 </div>
              </div>

            </div>
          </div>

          <!-- RIGHT COLUMN: Compliance Roadmap & Leave Protocol -->
          <div class="space-y-10">

            <!-- ANNUAL LEAVE PROTOCOL CARD -->
            <div v-if="isPersonalProfileView" class="bg-white rounded-[40px] shadow-2xl border border-slate-100 overflow-hidden animate-fadeIn text-left">
              <div class="p-8 bg-[#0A1630] text-white flex items-center justify-between">
                <div class="flex items-center gap-3">
                  <SunIcon :class="displayedProfile?.annualLeaveStatus ? 'text-amber-400' : 'text-slate-500'" class="h-5 w-5" />
                  <h2 class="text-[10px] font-black uppercase tracking-[0.2em]">Annual Leave Protocol</h2>
                </div>
                <div v-if="isUpdatingLeave" class="animate-spin h-4 w-4 border-2 border-[#20FCF1] border-t-transparent rounded-full"></div>
              </div>
              
              <div :class="displayedProfile?.annualLeaveStatus ? 'bg-amber-50/50' : 'bg-white'" class="p-8 space-y-6 transition-colors duration-500">
                <div class="flex items-center justify-between">
                  <div>
                    <p class="text-[11px] font-black text-[#0A1630] uppercase tracking-tight">Leave Mode Status</p>
                    <p class="text-[9px] text-slate-400 font-bold uppercase mt-1">
                      {{ displayedProfile?.annualLeaveStatus ? 'Duty Suspended' : 'Operational Duty' }}
                    </p>
                  </div>
                  <button 
                    @click="toggleAnnualLeave" 
                    :disabled="isUpdatingLeave"
                    :class="displayedProfile?.annualLeaveStatus ? 'bg-amber-500 shadow-amber-200' : 'bg-slate-200 shadow-slate-100'"
                    class="relative inline-flex h-8 w-14 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none shadow-lg"
                  >
                    <span 
                      aria-hidden="true" 
                      :class="displayedProfile?.annualLeaveStatus ? 'translate-x-6' : 'translate-x-0'"
                      class="pointer-events-none inline-block h-7 w-7 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out"
                    ></span>
                  </button>
                </div>

                <div class="p-4 bg-white rounded-2xl border border-slate-100 shadow-sm">
                  <p class="text-[9px] text-slate-500 leading-relaxed italic font-medium">
                    Activating Leave Mode will automatically redirect your Timesheet Authorisation alerts to your Assigned Reporting Director.
                  </p>
                </div>
              </div>
            </div>

            <!-- Induction Checklist -->
            <div class="bg-[#0A1630] p-10 rounded-[40px] shadow-2xl border-b-4 border-[#20FCF1]">
              <h2 class="text-[10px] font-black text-[#20FCF1] uppercase tracking-[0.3em] mb-10 border-b border-white/10 pb-6 flex items-center gap-3 text-left">
                <ClipboardDocumentCheckIcon class="h-6 w-6" /> Induction Record
              </h2>
              
              <div class="space-y-3 text-left">
                  <div v-for="(form, index) in filteredOnboardingRecord" :key="form.id" 
                    :class="['p-5 rounded-[24px] border flex items-center justify-between transition-all group text-left', 
                            form.isCompleted ? 'bg-[#20FCF1]/10 border-[#20FCF1]/20' : 'bg-white/5 border-white/5 hover:border-[#20FCF1]/50']"
                  >
                    <div class="flex items-center gap-4 min-w-0">
                      <CheckCircleIcon v-if="form.isCompleted" class="h-5 w-5 text-[#20FCF1] flex-shrink-0" />
                      <div v-else class="h-5 w-5 rounded-full border-2 border-white/20 flex-shrink-0"></div>
                      <span :class="form.isCompleted ? 'text-white' : 'text-slate-500'" class="truncate uppercase text-[10px] font-black tracking-tight pt-0.5">{{ index + 1 }}. {{ form.name }}</span>
                    </div>
                    
                    <router-link 
                      v-if="form.isCompleted && form.submissionId" 
                      :to="{ name: 'completedForm', params: { collectionName: 'onboardingFormSubmissions', submissionId: form.submissionId } }" 
                      class="text-[#20FCF1] text-[9px] font-black uppercase hover:underline whitespace-nowrap ml-4"
                    >View Audit</router-link>
                    
                    <router-link 
                      v-else-if="isPersonalProfileView" 
                      :to="form.route" 
                      class="text-white text-[9px] font-black uppercase hover:text-[#FD5E00] transition-colors"
                    >Start</router-link>
                    
                    <span v-else class="text-slate-600 text-[9px] font-black uppercase tracking-widest">Missing</span>
                  </div>
              </div>

              <div v-if="canDownloadPack" class="mt-12 text-left">
                <button @click="downloadAllCompletedForms" :disabled="isDownloading" class="w-full bg-[#20FCF1] text-[#0A1630] py-5 rounded-[24px] text-[10px] font-black uppercase tracking-[0.2em] hover:scale-[1.02] transition-all shadow-2xl disabled:opacity-50">
                  <span v-if="isDownloading">Generating Registry...</span>
                  <span v-else>Authorise Pack Export</span>
                </button>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, reactive, onMounted, watch, onUnmounted } from 'vue'; 
import { useUserStore } from '@/stores/userStore';
import { auth, db, firebaseApp } from '@/firebaseConfig'; 
import { signOut, sendPasswordResetEmail } from 'firebase/auth';
import { useRouter, useRoute } from 'vue-router';
import { collection, query, where, getDocs, doc, getDoc, deleteDoc, setDoc, onSnapshot, limit } from 'firebase/firestore'; 
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getStorage, ref as sRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import PendingActionsPanel from '@/components/dashboard/PendingActionsPanel.vue';
import QRCode from 'qrcode';

import { 
  ClipboardDocumentCheckIcon, UserIcon, CheckCircleIcon, 
  ShieldCheckIcon, StarIcon, PencilSquareIcon,
  EnvelopeIcon, ClipboardIcon, QrCodeIcon, SunIcon
} from '@heroicons/vue/24/outline';

const userStore = useUserStore();
const router = useRouter();
const route = useRoute();
const functions = getFunctions(firebaseApp, 'europe-west2');

const isDataLoading = ref(true);
const isUpdatingLeave = ref(false);
const displayedProfile = ref(null); 
const reviewQrUrl = ref('');
const clientFeedbackQrUrl = ref(''); // 🛡️ SEPARATED CLIENT FEEDBACK QR CODE
const copiedText = ref('Copy Review Link');
const copiedFeedbackText = ref('Copy Questionnaire Link'); // 🛡️ SEPARATED FEEDBACK COPY TEXT
const submissionsMap = reactive(new Map());
const isDownloading = ref(false);
const jobTitleOptions = ref([]);
let unsubscribeActions = null;

const isResettingMfa = ref(false);
const isSendingReset = ref(false);

// Inline state controls for Super Admin uprooted editing
const isEditingSuperAdmin = ref(false);
const isSavingSuperAdmin = ref(false);
const superAdminEditForm = ref({
  firstName: '',
  lastName: '',
  photoUrl: ''
});

// Photo upload local state refs
const fileInput = ref(null);
const isUploadingImage = ref(false);

const isOwnProfile = computed(() => {
  const currentUid = auth.currentUser?.uid;
  const targetUid = route.params.userId || route.params.id;
  return !targetUid || targetUid === currentUid;
});

const isPersonalProfileView = computed(() => isOwnProfile.value);
const isUserActive = computed(() => displayedProfile.value?.status?.toLowerCase() === 'active');

// Determines if the current profile being rendered belongs to a Super Admin
const isSuperAdminProfile = computed(() => {
  if (!displayedProfile.value) return false;
  return displayedProfile.value.role === 'super-admin' || (isOwnProfile.value && userStore.role === 'super-admin');
});

// Determines if the logged-in administrator possesses MFA clearance authority
const canManageMfa = computed(() => {
  return userStore.role === 'admin' || userStore.role === 'super-admin' || userStore.hasAdminAccess;
});

const editProfileRoute = computed(() => {
  const targetUid = route.params.userId || route.params.id || auth.currentUser?.uid;
  
  if (isSuperAdminProfile.value) {
    return `/admin/users/edit/${targetUid}`;
  }
  
  return (userStore.isAdmin && !isOwnProfile.value) 
    ? `/admin/users/edit/${targetUid}` 
    : '/forms/your-details';
});

// Setup form defaults before editing
const startEditingSuperAdmin = () => {
  superAdminEditForm.value = {
    firstName: displayedProfile.value?.firstName || '',
    lastName: displayedProfile.value?.lastName || '',
    photoUrl: displayedProfile.value?.photoUrl || ''
  };
  isEditingSuperAdmin.value = true;
};

// Triggers native click on our hidden input file selector
const triggerFileInput = () => {
  fileInput.value?.click();
};

// Orchestrates file selection, Cloud Storage upload, and instant state synchronization
const handleImageUpload = async (event) => {
  const file = event.target.files?.[0];
  if (!file) return;

  // Max 5MB payload limit
  if (file.size > 5 * 1024 * 1024) {
    alert("Security Seal: Upload limited to maximum of 5MB files.");
    return;
  }

  isUploadingImage.value = true;
  try {
    const storageInstance = getStorage(firebaseApp);
    const targetUid = displayedProfile.value?.userId || route.params.userId || route.params.id || auth.currentUser?.uid;
    
    if (!targetUid) {
      throw new Error("Target User ID is unresolved in registry.");
    }

    // Direct, unique storage path configuration
    const storagePath = `userProfiles/${targetUid}/avatar_${Date.now()}`;
    const fileRef = sRef(storageInstance, storagePath);
    
    const snapshot = await uploadBytes(fileRef, file);
    const downloadUrl = await getDownloadURL(snapshot.ref);
    
    // Direct Database Synchronization
    const docRef = doc(db, 'userProfiles', targetUid);
    await setDoc(docRef, { photoUrl: downloadUrl }, { merge: true });
    
    // Synchronise all reactive variables instantly
    if (displayedProfile.value) {
      displayedProfile.value.photoUrl = downloadUrl;
    }
    superAdminEditForm.value.photoUrl = downloadUrl;
    
    alert("Avatar Signature successfully synchronized with Cloud Storage and Firestore Registry.");
  } catch (err) {
    console.error("Image upload failure details:", err);
    if (err.code === 'storage/unauthorized' || err.message?.includes('unauthorized')) {
      alert(
        `🛡️ Sentinel Security Rule Restriction:\n\n` +
        `Your Firebase Storage rules likely restrict uploads to users with an assigned 'clientId' (multi-tenancy scoping).\n` +
        `Because this Super Admin account is uprooted and client-free, Storage has blocked the write.\n\n` +
        `To resolve this, please update your 'storage.rules' file to authorize the 'super-admin' role:\n\n` +
        `allow write: if request.auth != null && (\n` +
        `  firestore.get(/databases/$(database)/documents/userProfiles/$(request.auth.uid)).data.role == 'super-admin'\n` +
        `);`
      );
    } else {
      alert(`Image transmission failed: ${err.message}`);
    }
  } finally {
    isUploadingImage.value = false;
  }
};

// Direct, clientless database write for Super Admin account details
const saveSuperAdminProfile = async () => {
  if (!displayedProfile.value?.userId) return;
  isSavingSuperAdmin.value = true;
  try {
    const docRef = doc(db, 'userProfiles', displayedProfile.value.userId);
    await setDoc(docRef, {
      firstName: superAdminEditForm.value.firstName,
      lastName: superAdminEditForm.value.lastName,
      photoUrl: superAdminEditForm.value.photoUrl
    }, { merge: true });
    
    // Update local reactive layout
    displayedProfile.value.firstName = superAdminEditForm.value.firstName;
    displayedProfile.value.lastName = superAdminEditForm.value.lastName;
    displayedProfile.value.photoUrl = superAdminEditForm.value.photoUrl;
    
    alert("Supervisor Profile changes synchronized successfully.");
    isEditingSuperAdmin.value = false;
  } catch (err) {
    console.error("Super Admin Profile Write Failed:", err);
    alert("Synchronisation failure. Check secure write permissions.");
  } finally {
    isSavingSuperAdmin.value = false;
  }
};

const toggleAnnualLeave = async () => {
  if (!displayedProfile.value?.userId) return;
  isUpdatingLeave.value = true;
  const newStatus = !displayedProfile.value.annualLeaveStatus;
  try {
    await setDoc(doc(db, 'userProfiles', displayedProfile.value.userId), {
      annualLeaveStatus: newStatus
    }, { merge: true });
    displayedProfile.value.annualLeaveStatus = newStatus;
  } catch (err) {
    console.error("Leave Registry Handshake Failure:", err);
  } finally {
    isUpdatingLeave.value = false;
  }
};

const handleUpdateCount = (count) => { if (isOwnProfile.value) userStore.setGlobalPendingActionCount(count); };
const canDownloadPack = computed(() => userStore.role === 'admin' || userStore.role === 'super-admin');

const fetchJobTitles = async (clientId) => {
  if (!clientId) return;
  try {
    const snap = await getDoc(doc(db, 'formOptions', 'jobTitles'));
    if (snap.exists()) jobTitleOptions.value = snap.data()[clientId] || snap.data().global || [];
  } catch (e) { console.error("Job Title Registry Failure:", e); }
};

const formattedJobTitle = computed(() => {
  const val = displayedProfile.value?.jobTitle;
  if (!val) return 'Registry Entry';
  const match = jobTitleOptions.value.find(o => o.value === val);
  return match ? match.text : val.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
});

const shareViaEmail = () => {
  const name = `${displayedProfile.value.firstName} ${displayedProfile.value.lastName}`;
  const reviewUrl = `${window.location.origin}/public/review/${displayedProfile.value.userId}`;
  const subject = encodeURIComponent("Performance Review Request: " + name);
  const body = encodeURIComponent("Hello,\n\nI would appreciate it if you could take a moment to provide performance feedback using the link below:\n\n" + reviewUrl + "\n\nThank you.");
  window.location.href = "mailto:?subject=" + subject + "&body=" + body;
};

const copyReviewLink = () => {
  const reviewUrl = `${window.location.origin}/public/review/${displayedProfile.value.userId}`;
  navigator.clipboard.writeText(reviewUrl).then(() => {
    copiedText.value = 'Copied to Clipboard!';
    setTimeout(() => { copiedText.value = 'Copy Review Link'; }, 2500);
  });
};

// 🛡️ SEPARATED CLIENT FEEDBACK QR ACTIONS
const shareFeedbackViaEmail = () => {
  const name = `${displayedProfile.value.firstName} ${displayedProfile.value.lastName}`;
  const feedbackUrl = `${window.location.origin}/public/client-feedback/${displayedProfile.value.userId}`;
  const subject = encodeURIComponent("Client Feedback Questionnaire: " + name);
  const body = encodeURIComponent("Hello,\n\nPlease take a moment to complete our client feedback questionnaire regarding our worksite performance using the link below:\n\n" + feedbackUrl + "\n\nThank you.");
  window.location.href = "mailto:?subject=" + subject + "&body=" + body;
};

const copyFeedbackLink = () => {
  const feedbackUrl = `${window.location.origin}/public/client-feedback/${displayedProfile.value.userId}`;
  navigator.clipboard.writeText(feedbackUrl).then(() => {
    copiedFeedbackText.value = 'Copied to Clipboard!';
    setTimeout(() => { copiedFeedbackText.value = 'Copy Questionnaire Link'; }, 2500);
  });
};

/** 🛡️ COMPLIANCE ROADMAP: Form sequence and original names restored */
const baseOnboardingForms = [
  { id: 'yourDetails', name: 'Your Details', route: '/forms/your-details' },
  { id: 'employeeInduction', name: 'Induction', route: '/forms/employee-induction' }, 
  { id: 'rolesAndResponsibilities', name: 'Responsibility Acknowledgement', route: '/forms/roles-responsibilities' },
  { id: 'literacyTest', name: 'Literacy Test', route: '/forms/literacy-test' },
  { id: 'annualHealthAssessment', name: 'Health Assessment', route: '/forms/annual-health-assessment' },
  { id: 'eyesightDeclaration', name: 'Eyesight Declaration', route: '/forms/eyesight-declaration' },
  { id: 'rightToWork', name: 'Right to Work', route: '/forms/right-to-work' },
  { id: 'ppeRequest', name: 'PPE Issue', route: '/forms/ppe-request' },
  { id: 'primarySponsorContract', name: 'Primary Sponsor Contract', route: '/forms/primary-sponsor-contract' },
  { id: 'employeeHandbookSignOff', name: 'Employee Handbook Acknowledgement', route: '/forms/employee-handbook-sign-off', employeeOnly: true },
  { id: 'subContractorPack', name: 'Sub-Contractor Pack', route: '/forms/sub-contractor-pack', subContractorOnly: true },
];

const userCategory = computed(() => {
  if (!displayedProfile.value) return 'employee';
  const classification = (
    displayedProfile.value.employmentClassification || 
    displayedProfile.value.employmentType || 
    ''
  ).toLowerCase().trim();
  if (classification.includes('sub') && classification.includes('contract')) return 'sub-contractor';
  if (classification.includes('self') || classification === 'cis') return 'self-employed';
  return 'employee';
});

const filteredOnboardingRecord = computed(() => {
  if (!displayedProfile.value) return [];

  const isPrimarySponsor = String(displayedProfile.value.sponsorshipType || '').trim().toLowerCase() === 'primary';

  const filtered = baseOnboardingForms.filter(form => {
    if (userCategory.value === 'sub-contractor') return form.subContractorOnly;
    if (form.subContractorOnly) return false;

    // Form 9 (Primary Sponsor Contract) is strictly hidden from non-primary sponsor users
    if (form.id === 'primarySponsorContract') {
      return isPrimarySponsor;
    }

    if (form.employeeOnly && userCategory.value !== 'employee') return false;
    return true;
  });
  return filtered.map(form => ({ 
    ...form, 
    isCompleted: submissionsMap.has(form.id), 
    submissionId: submissionsMap.get(form.id) || null 
  }));
});

const handleLogout = async () => { try { await signOut(auth); router.push('/login'); } catch (err) { console.error("Logout Failure:", err); } };

const clearAllNotifications = async () => {
  if (!confirm("Confirm: Hide all notifications?")) return;
  try {
    const q = query(collection(db, 'user_actions'), where('targetUserId', '==', displayedProfile.value.userId));
    const snap = await getDocs(q);
    await Promise.all(snap.docs.map(d => deleteDoc(doc(db, 'user_actions', d.id))));
    await userStore.deleteAllNotifications();
    userStore.setGlobalPendingActionCount(0);
  } catch (e) { console.error("Registry Cleanup Failure:", e); }
};

const downloadAllCompletedForms = async () => {
  isDownloading.value = true;
  const completed = filteredOnboardingRecord.value.filter(f => f.isCompleted && f.submissionId);
  try {
    const fn = httpsCallable(functions, 'generatePdf', { timeout: 120000 });
    for (const f of completed) {
      const res = await fn({ collectionName: 'onboardingFormSubmissions', submissionId: f.submissionId, formIdentifier: f.id });
      if (res.data?.downloadUrl) window.open(res.data.downloadUrl, '_blank');
      await new Promise(r => setTimeout(r, 1500));
    }
  } catch (err) { console.error("Export Failure:", err); } finally { isDownloading.value = false; }
};

// Dispatches credential reset sequence for the Super Admin
const triggerPasswordReset = async () => {
  const email = displayedProfile.value?.email;
  if (!email) {
    alert("No registered email signature detected on the active profile.");
    return;
  }
  
  if (!confirm(`Synchronise Security Seal? A credential reset handshake link will be dispatched to: ${email}`)) return;
  
  isSendingReset.value = true;
  try {
    await sendPasswordResetEmail(auth, email);
    alert("Security Seal Synchronised: Reset handshake email successfully transmitted.");
  } catch (err) {
    console.error("Credentials Reset Failure:", err);
    alert(`Failed to transmit security link: ${err.message}`);
  } finally {
    isSendingReset.value = false;
  }
};

// Triggers programmatic administrative unenrollment of second factors
const triggerMfaReset = async () => {
  const targetUid = route.params.userId || route.params.id;
  if (!targetUid) return;

  const targetName = `${displayedProfile.value?.firstName || ''} ${displayedProfile.value?.lastName || ''}`.trim();
  if (!confirm(`Are you sure you want to perform an Emergency MFA Reset for ${targetName || 'this user'}? This will clear all SMS authentication factors and permit standard password login.`)) return;

  isResettingMfa.value = true;
  try {
    const resetMfaFunc = httpsCallable(functions, 'adminResetUserMFA');
    const res = await resetMfaFunc({ targetUserId: targetUid });
    
    if (res.data && res.data.success) {
      alert("Emergency Reset Executed: SMS factors cleared, email verified on server, and user marked as exempt.");
      await syncProfileData();
    } else {
      throw new Error(res.data ? res.data.message : 'Registry response empty.');
    }
  } catch (err) {
    console.error("MFA Handshake Clear Failure:", err);
    alert("Emergency reset failed: " + err.message);
  } finally {
    isResettingMfa.value = false;
  }
};

/** 🛡️ PHASE 12: DUAL-LOOKUP & CLIENT-LOCK RECOVERY */
const syncProfileData = async () => {
  isDataLoading.value = true;
  const targetUid = route.params.userId || route.params.id || auth.currentUser?.uid;
  if (!targetUid) return;

  if (unsubscribeActions) unsubscribeActions();
  unsubscribeActions = onSnapshot(query(collection(db, 'user_actions'), where('targetUserId', '==', targetUid)), () => {
    // Actions snapshot listener active
  });

  try {
    const pSnap = await getDoc(doc(db, 'userProfiles', targetUid));
    if (pSnap.exists()) {
      const pData = pSnap.data();
      displayedProfile.value = { ...pData, userId: pSnap.id };
      await fetchJobTitles(pData.clientId);
      
      // 1. User ID QR Code Matrix
      const reviewUrl = `${window.location.origin}/public/review/${targetUid}`;
      reviewQrUrl.value = await QRCode.toDataURL(reviewUrl, { width: 300, margin: 1 });

      // 2. Client Feedback Questionnaire QR Code Matrix
      const feedbackUrl = `${window.location.origin}/public/client-feedback/${targetUid}`;
      clientFeedbackQrUrl.value = await QRCode.toDataURL(feedbackUrl, { width: 300, margin: 1 });

      submissionsMap.clear();

      /** 🛡️ STRATEGIC FIX: FETCHING BY BOTH UID AND EMAIL WITH CLIENT-ID SCOPE */
      // 1. Fetch by User ID
      const qId = query(collection(db, "onboardingFormSubmissions"), where("userId", "==", targetUid));
      const sId = await getDocs(qId);
      sId.forEach((d) => { if (d.data().formIdentifier) submissionsMap.set(d.data().formIdentifier, d.id); });

      // 2. Fallback fetch by Email
      if (pData.email) {
          const qEmail = query(
            collection(db, "onboardingFormSubmissions"), 
            where("email", "==", pData.email.toLowerCase().trim()),
            limit(50)
          );
          const sEmail = await getDocs(qEmail);
          sEmail.forEach((d) => { 
            const data = d.data();
            if (data.formIdentifier) submissionsMap.set(data.formIdentifier, d.id); 
          });
      }
    }
  } catch (e) { console.error("Registry Sync Failure:", e); } finally { isDataLoading.value = false; }
};

// Re-evaluate profile data as soon as the browser session is authenticated
watch(() => userStore.isAuthReady, (ready) => {
  if (ready) {
    syncProfileData();
  }
}, { immediate: true });

watch(() => route.params, syncProfileData, { deep: true });

onMounted(() => {
  if (userStore.isAuthReady) {
    syncProfileData();
  }
});

onUnmounted(() => { if (unsubscribeActions) unsubscribeActions(); });
</script>

<style scoped>
.animate-fadeIn { animation: fadeIn 0.4s ease-out forwards; }
@keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
.pulse-animation { animation: pulse-red 2s infinite; }
@keyframes pulse-red { 0% { box-shadow: 0 0 0 0 rgba(220, 38, 38, 0.7); } 70% { box-shadow: 0 0 0 10px rgba(220, 38, 38, 0); } 100% { box-shadow: 0 0 0 0 rgba(220, 38, 38, 0); } }
.no-scrollbar::-webkit-scrollbar { display: none; }
</style>